/*
  The cart drawer (2026-09-28): the <cart-drawer> element for snippets/cart-drawer.liquid. Replaces Dawn's
  cart-drawer.js and keeps its public contract, because product-form.js, the product page's engraving add,
  the collection quick add, cart.js and standard-actions-override.js call it:
    open(trigger), close(), renderContents(parsedState), getSectionsToRender(), getSectionInnerHTML(),
    setActiveElement(el), the is-empty class, #CartDrawer and #CartDrawer-Overlay.
  Everything the drawer changes goes through the Ajax cart with sections=cart-drawer,cart-icon-bubble and
  swaps #CartDrawer's inside, keeping the scroll, the focus and the meters' motion. Also: the quantity
  stepper (debounced), removal with a six-second undo (a knife goes with its engraving), gift wrap and its
  message, the order note, one-tap pairs, the counted total, and Shopify's standard cart events.
*/
(() => {
  'use strict';
  if (customElements.get('cart-drawer')) return;

  const R = () => window.routes || {};
  const url = (k, d) => R()[k] || d;
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const UNDO_MS = 6000;

  function money(cents, like) {
    /* the drawer's own format, read from a price the server printed, so the currency sign matches */
    const sample = (like || '').trim();
    const m = sample.match(/^([^\d]*)[\d.,\s]+([^\d]*)$/);
    const pre = m ? m[1] : '$', post = m ? m[2] : '';
    const n = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return pre + n + post;
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.timers = {};
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('keydown', (e) => this.onKey(e));
      /* Esc closes the drawer wherever the focus is */
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('active') && !this.contains(e.target)) this.close(); });
      this.bindIcon();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (ev) => {
          if (!ev || ev.source === 'crk-cart-drawer' || ev.source === 'product-form') return;
          return this.reload();
        });
      }
      this.defineViewEvent();
    }

    /* ---------- Dawn's contract ---------- */
    getSectionsToRender() { return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }]; }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return el ? el.innerHTML : '';
    }
    setActiveElement(el) { this.activeElement = el; }
    renderContents(parsed) {
      this.apply(parsed);
      this.open();
    }

    bindIcon() {
      const icon = document.getElementById('cart-icon-bubble');
      if (!icon || icon.dataset.cdBound) return;
      icon.dataset.cdBound = '1';
      icon.setAttribute('role', 'button');
      icon.setAttribute('aria-haspopup', 'dialog');
      icon.addEventListener('click', (e) => { e.preventDefault(); this.open(icon); });
      icon.addEventListener('keydown', (e) => { if (e.code === 'Space') { e.preventDefault(); this.open(icon); } });
    }

    open(trigger) {
      if (trigger) this.setActiveElement(trigger);
      if (this.classList.contains('active')) return;
      if (!this.activeElement) this.activeElement = document.activeElement;
      this.classList.add('is-opening', 'active', 'animate');
      clearTimeout(this.timers.opening);
      this.timers.opening = setTimeout(() => this.classList.remove('is-opening'), 1200);
      document.documentElement.classList.add('crk-cd-lock');
      /* aria-modal made true: everything else on the page is inert while the drawer is open */
      this.inerted = Array.from(document.body.children).filter((el) => el !== this && !el.contains(this) && !el.inert && el.tagName !== 'SCRIPT');
      this.inerted.forEach((el) => { el.inert = true; });
      /* focus enters at once, so the first Tab is already inside */
      const panel = this.querySelector('[data-cd-panel]');
      if (panel) panel.focus({ preventScroll: true });
      this.measure();
      this.recent();
      const v = this.querySelector('crk-cart-view');
      if (v && v.dispatchViewEvent) { try { v.dispatchViewEvent(); } catch (e) { /* analytics only */ } }
    }

    close() {
      if (!this.classList.contains('active')) return;
      this.classList.remove('active', 'animate');
      document.documentElement.classList.remove('crk-cd-lock');
      (this.inerted || []).forEach((el) => { el.inert = false; });
      this.inerted = null;
      const back = this.activeElement;
      this.activeElement = null;
      if (back && back.isConnected && back.focus) back.focus({ preventScroll: true });
    }

    /* ---------- the Ajax cart ---------- */
    post(path, body) {
      body.sections = SECTIONS;
      body.sections_url = window.location.pathname;
      return fetch(path, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then((r) => r.json().then((j) => { if (!r.ok || j.status) throw new Error(j.description || j.message || 'The cart did not update.'); return j; }));
    }

    reload() {
      return fetch(url('cart_url', '/cart') + '?sections=' + SECTIONS.join(','), { credentials: 'same-origin' })
        .then((r) => r.json()).then((sections) => this.apply({ sections })).catch(() => {});
    }

    /* swap #CartDrawer's inside, keeping the scroll, the focus and the motion of the meters and the total */
    apply(parsed) {
      if (!parsed || !parsed.sections) return;
      const html = parsed.sections['cart-drawer'];
      const bubble = parsed.sections['cart-icon-bubble'];
      if (bubble) {
        const icon = document.getElementById('cart-icon-bubble');
        if (icon) icon.innerHTML = this.getSectionInnerHTML(bubble);
      }
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('#CartDrawer');
      const host = doc.querySelector('cart-drawer');
      const box = this.querySelector('#CartDrawer');
      if (!fresh || !box) return;
      const scroll = this.querySelector('[data-cd-scroll]');
      const top = scroll ? scroll.scrollTop : 0;
      const hadFocus = !!(document.activeElement && this.contains(document.activeElement));
      const focusId = hadFocus ? this.focusKey(document.activeElement) : null;
      const oldMeter = this.querySelector('[data-cd-meter]');
      const oldP = oldMeter ? oldMeter.style.getPropertyValue('--p') : '';
      const oldTotal = this.querySelector('[data-cd-total]');
      const fromCents = oldTotal ? parseInt(oldTotal.dataset.cdTotal, 10) : null;

      box.innerHTML = fresh.innerHTML;
      this.classList.toggle('is-empty', host ? host.classList.contains('is-empty') : false);

      const s2 = this.querySelector('[data-cd-scroll]');
      if (s2) s2.scrollTop = top;
      /* the meter grows from where it was */
      const meter = this.querySelector('[data-cd-meter]');
      if (meter && oldP && !reduce()) {
        const to = meter.style.getPropertyValue('--p');
        meter.style.setProperty('--p', oldP);
        void meter.offsetWidth;
        requestAnimationFrame(() => meter.style.setProperty('--p', to));
      }
      /* the total counts to its new value */
      const total = this.querySelector('[data-cd-total]');
      if (total && fromCents != null) this.countTo(total, fromCents, parseInt(total.dataset.cdTotal, 10));
      /* focus never falls out of an open drawer: back to the same control, or to the panel */
      if (hadFocus || (this.classList.contains('active') && !this.contains(document.activeElement))) {
        const again = focusId ? this.querySelector(focusId) : null;
        const p = this.querySelector('[data-cd-panel]');
        if (again) again.focus({ preventScroll: true }); else if (p) p.focus({ preventScroll: true });
      }
      this.measure();
      if (this.classList.contains('is-empty')) this.recent();
    }

    focusKey(el) {
      if (el.dataset.focusId) return '[data-focus-id="' + el.dataset.focusId + '"]';
      /* a line's key changes on every update, its variant does not */
      const line = el.closest('[data-fid]');
      const step = el.getAttribute('data-cd-step');
      if (line && step) return '[data-fid="' + line.dataset.fid + '"] [data-cd-step="' + step + '"]';
      if (el.hasAttribute('data-cd-gift')) return '[data-cd-gift]';
      return null;
    }

    countTo(el, from, to) {
      const btn = this.querySelector('[data-cd-total-btn]');
      if (from === to || reduce()) return;
      const like = el.textContent, likeBtn = btn ? btn.textContent : '';
      const t0 = performance.now(), dur = 520;
      const step = (t) => {
        const k = Math.min(1, (t - t0) / dur), v = Math.round(from + (to - from) * (1 - Math.pow(1 - k, 3)));
        if (k < 1) {
          el.textContent = money(v, like.replace(/\s*[A-Z]{3}$/, '')) + (like.match(/\s[A-Z]{3}$/) || [''])[0];
          if (btn) btn.textContent = money(v, likeBtn);
          requestAnimationFrame(step);
        } else { el.textContent = like; if (btn) btn.textContent = likeBtn; }
      };
      requestAnimationFrame(step);
    }

    measure() {
      const foot = this.querySelector('.cd__foot');
      this.style.setProperty('--cd-foot', (foot ? foot.offsetHeight : 0) + 'px');
    }

    busy(el, on) { if (el) el.classList.toggle('is-busy', on); }
    say(msg) { const l = this.querySelector('[data-cd-live]'); if (l) l.textContent = msg; }
    fail(err, el) {
      this.busy(el, false);
      this.say(err.message);
      this.toast(err.message, null);
      this.reload();
    }

    /* Shopify's standard events, the way Dawn's cart.js sends them */
    linesEvent(action, lines) {
      const SE = window.StandardEvents || {};
      if (!SE.CartLinesUpdateEvent) return null;
      try {
        const d = SE.CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(new SE.CartLinesUpdateEvent({ action, context: 'dialog', lines, promise: d.promise }));
        return d;
      } catch (e) { return null; }
    }
    settle(d, cart) {
      const SE = window.StandardEvents || {};
      if (d && SE.CartLinesUpdateEvent) { try { d.resolve({ cart: SE.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) }); } catch (e) { /* analytics only */ } }
    }
    defineViewEvent() {
      const SE = window.StandardEvents;
      if (!SE || !SE.createViewEventElement || customElements.get('crk-cart-view')) return;
      try { customElements.define('crk-cart-view', SE.createViewEventElement(HTMLElement)); } catch (e) { /* analytics only */ }
    }
    told(cart) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'crk-cart-drawer', cartData: cart });
    }

    /* set quantities by line key: {key: qty}; 0 removes */
    update(updates, el, action) {
      this.busy(el, true);
      const d = this.linesEvent(action || 'update', Object.keys(updates).map((k) => ({ id: k, quantity: updates[k] })));
      return this.post(url('cart_update_url', '/cart/update') + '.js', { updates })
        .then((cart) => { this.settle(d, cart); this.apply(cart); this.told(cart); return cart; })
        .catch((err) => { if (d) d.reject(err); this.fail(err, el); throw err; });
    }

    add(items, el) {
      this.busy(el, true);
      return this.post(url('cart_add_url', '/cart/add') + '.js', { items })
        .then(() => this.reload()).then(() => this.told())
        .catch((err) => this.fail(err, el));
    }

    /* ---------- events ---------- */
    onClick(e) {
      const t = e.target.closest('button, [data-cd-close], a');
      if (!t || !this.contains(t)) return;
      if (t.hasAttribute('data-cd-close')) { e.preventDefault(); this.close(); return; }
      if (t.hasAttribute('data-cd-step')) { this.step(t); return; }
      if (t.hasAttribute('data-cd-remove')) { this.remove(t); return; }
      if (t.hasAttribute('data-cd-gift')) { this.gift(t); return; }
      if (t.hasAttribute('data-cd-add')) { t.classList.add('is-busy'); this.add([{ id: Number(t.dataset.cdAdd), quantity: 1 }], t); return; }
      if (t.hasAttribute('data-cd-undo')) { this.undo(); return; }
      if (t.tagName === 'A' && t.getAttribute('href') && !t.target) this.close();
    }

    step(btn) {
      const box = btn.closest('[data-cd-qty]'), line = btn.closest('[data-key]');
      const out = box.querySelector('output');
      const q = Math.max(0, parseInt(out.textContent, 10) + parseInt(btn.dataset.cdStep, 10));
      if (q === 0) { this.remove(line.querySelector('.cd-line__rm[data-cd-remove]')); return; }
      out.textContent = q;
      box.classList.remove('is-bump'); void box.offsetWidth; box.classList.add('is-bump');
      /* several quick taps make one request. It is keyed by the variant, because a refresh in between gives
         the line a new key and new nodes: the request reads the line as it is when it leaves */
      const fid = line.dataset.fid || box.dataset.cdQty;
      clearTimeout(this.timers['q' + fid]);
      this.timers['q' + fid] = setTimeout(() => {
        const live = this.querySelector('[data-fid="' + fid + '"]') || line;
        const lbox = live.querySelector('[data-cd-qty]') || box;
        const key = lbox.dataset.cdQty;
        const lout = lbox.querySelector('output'); if (lout) lout.textContent = q;
        this.update({ [key]: q }, live).then((cart) => {
          const got = cart && cart.items ? cart.items.find((i) => i.key === key) : null;
          if (got && got.quantity !== q) this.say('Only ' + got.quantity + ' can be ordered.');
        }).catch(() => {});
      }, 380);
    }

    remove(btn) {
      if (!btn) return;
      const key = btn.dataset.cdRemove;
      const also = (btn.dataset.also || '').split(',').filter(Boolean);
      const line = btn.closest('.cd-line');
      const updates = { [key]: 0 };
      also.forEach((k) => { updates[k] = 0; });
      /* what undo puts back: the same lines, with their properties */
      const keys = [key, ...also];
      const title = btn.dataset.title || 'the item';
      /* the snapshot is read BEFORE the removal, so undo always has the lines to put back */
      const snap = fetch(url('cart_url', '/cart') + '.js', { credentials: 'same-origin' }).then((r) => r.json()).then((cart) => {
        this.undoItems = cart.items.filter((i) => keys.includes(i.key)).map((i) => ({ id: i.variant_id, quantity: i.quantity, properties: i.properties || {} }));
      }).catch(() => { this.undoItems = null; });
      const out = line && !reduce() && btn.classList.contains('cd-line__rm');
      if (out) line.classList.add('is-going');
      Promise.all([snap, new Promise((r) => setTimeout(r, out ? 260 : 0))])
        .then(() => this.update(updates, line, 'remove'))
        .then(() => { this.toast('Removed ' + title + '.', true); this.say('Removed ' + title + '.'); })
        .catch(() => { if (line) line.classList.remove('is-going'); });
    }

    undo() {
      const items = this.undoItems;
      this.toast(null);
      if (!items || !items.length) return;
      this.undoItems = null;
      this.add(items).then(() => this.say('Put back.'));
    }

    toast(msg, canUndo) {
      const t = this.querySelector('[data-cd-toast]');
      if (!t) return;
      clearTimeout(this.timers.toast);
      if (!msg) { t.hidden = true; return; }
      t.querySelector('[data-cd-toast-txt]').textContent = msg;
      t.querySelector('[data-cd-undo]').hidden = !canUndo;
      t.querySelector('.cd-toast__ring').style.display = canUndo ? '' : 'none';
      t.hidden = false;
      /* restart the ring */
      const c = t.querySelector('circle'); c.style.animation = 'none'; void c.getBoundingClientRect(); c.style.animation = '';
      this.timers.toast = setTimeout(() => { t.hidden = true; this.undoItems = null; }, UNDO_MS);
    }

    gift(sw) {
      sw.classList.add('is-busy');
      const on = sw.getAttribute('aria-checked') === 'true';
      sw.setAttribute('aria-checked', String(!on));
      if (on && sw.dataset.key) this.update({ [sw.dataset.key]: 0 }, null, 'remove').catch(() => {});
      else this.add([{ id: Number(sw.dataset.variant), quantity: 1 }], null);
    }

    onChange(e) {
      const t = e.target;
      if (t.hasAttribute('data-cd-gift-msg')) {
        const val = t.value.trim();
        this.post(url('cart_change_url', '/cart/change') + '.js', { id: t.dataset.key, quantity: 1, properties: val ? { Message: val } : {} })
          .then((cart) => { this.apply(cart); this.say(val ? 'Card message saved.' : 'Card message cleared.'); }).catch((err) => this.fail(err));
      } else if (t.hasAttribute('data-cd-note')) {
        this.post(url('cart_update_url', '/cart/update') + '.js', { note: t.value })
          .then(() => this.say('Note saved.')).catch((err) => this.fail(err));
      }
    }

    onKey(e) {
      if (!this.classList.contains('active')) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const panel = this.querySelector('[data-cd-panel]');
      const f = Array.from(panel.querySelectorAll('a[href],button:not([disabled]),input,textarea,summary,[tabindex="0"]')).filter((x) => x.offsetParent !== null);
      const toast = this.querySelector('[data-cd-toast]:not([hidden]) button:not([hidden])');
      if (toast) f.push(toast);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---------- an empty cart shows what this browser viewed last (crk_rv, written by the product page) ---------- */
    recent() {
      const box = this.querySelector('[data-cd-recent]');
      if (!box || box.dataset.done) return;
      box.dataset.done = '1';
      let hs = [];
      try { hs = JSON.parse(localStorage.getItem('crk_rv') || '[]').slice(0, 3); } catch (e) { return; }
      if (!hs.length) return;
      const base = (R().root || '/').replace(/\/?$/, '/') + 'products/';
      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      Promise.all(hs.map((h) => fetch(base + encodeURIComponent(h) + '?view=data', { credentials: 'same-origin' }).then((r) => (r.ok ? r.json() : null)).catch(() => null)))
        .then((rows) => {
          rows = rows.filter((p) => p && p.h);
          if (!rows.length) return;
          const like = (this.querySelector('.crk-price') || { textContent: '$0.00' }).textContent;
          box.querySelector('[data-cd-recent-row]').innerHTML = rows.map((p) =>
            '<li class="cd-pair"><a class="cd-pair__img" href="' + esc(base + p.h) + '" tabindex="-1" aria-hidden="true"><img src="' + esc((p.i || '').replace(/width=\d+/, 'width=240')) + '" alt="" width="120" height="120" loading="lazy"></a>'
            + '<a class="cd-pair__title" href="' + esc(base + p.h) + '">' + esc(p.t) + '</a><span class="cd-pair__price">' + esc(money(p.p, like)) + '</span></li>').join('');
          box.hidden = false;
        });
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
